import { useMemo } from "react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { DealCard } from "./DealCard";
import { DealLeadPlaceholderCard } from "./DealLeadPlaceholderCard";
import { dealHasAttributedValue } from "@/lib/deals-funnel-filter";
import type { DealsFunnelLeadPlaceholder } from "@/hooks/useDealsFunnelLeadsWithoutDeal";
import type { DealWithStage, DealStage } from "@/types/deal";

interface DealsKanbanProps {
  deals: DealWithStage[];
  stages: DealStage[];
  leadPlaceholders?: DealsFunnelLeadPlaceholder[];
  onDealClick: (deal: DealWithStage) => void;
  onMoveDeal: (dealId: string, toStageId: string) => void;
  onLeadPlaceholderClick?: (lead: DealsFunnelLeadPlaceholder) => void;
}

function formatCurrency(v: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);
}

function sumStageDealAmount(stageDeals: DealWithStage[], stage: DealStage): number {
  return stageDeals.reduce((s, d) => {
    if (!dealHasAttributedValue(d)) return s;
    const amount = Number(d.amount);
    if (stage.is_closed_won) return d.status === "won" ? s + amount : s;
    if (stage.is_closed_lost) return d.status === "lost" ? s + amount : s;
    return d.status === "open" ? s + amount : s;
  }, 0);
}

export function DealsKanban({
  deals,
  stages,
  leadPlaceholders = [],
  onDealClick,
  onMoveDeal,
  onLeadPlaceholderClick,
}: DealsKanbanProps) {
  const firstOpenStageId = useMemo(
    () => stages.find(s => !s.is_closed_won && !s.is_closed_lost)?.id ?? null,
    [stages],
  );

  const dealsByStage = useMemo(() => {
    const map = new Map<string, DealWithStage[]>();
    stages.forEach(s => map.set(s.id, []));
    const wonStage = stages.find(s => s.is_closed_won);
    const lostStage = stages.find(s => s.is_closed_lost);

    deals.forEach(d => {
      let list = map.get(d.stage_id);
      if (!list && d.status === "won" && wonStage) {
        list = map.get(wonStage.id);
      }
      if (!list && d.status === "lost" && lostStage) {
        list = map.get(lostStage.id);
      }
      if (list) list.push(d);
    });
    return map;
  }, [deals, stages]);

  return (
    <ScrollArea className="w-full">
      <div className="flex gap-4 pb-4 min-w-max px-1">
        {stages.map(stage => {
          const stageDeals = dealsByStage.get(stage.id) ?? [];
          const stageLeadPlaceholders =
            stage.id === firstOpenStageId ? leadPlaceholders : [];
          const cardCount = stageDeals.length + stageLeadPlaceholders.length;
          const total = sumStageDealAmount(stageDeals, stage);
          const totalLabel =
            total > 0 ? formatCurrency(total) : cardCount > 0 ? "Sem valor atribuído" : formatCurrency(0);
          return (
            <div
              key={stage.id}
              className="w-[280px] flex-shrink-0 flex flex-col rounded-xl bg-muted/40 border"
              onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add("ring-2", "ring-primary/40"); }}
              onDragLeave={e => { e.currentTarget.classList.remove("ring-2", "ring-primary/40"); }}
              onDrop={e => {
                e.currentTarget.classList.remove("ring-2", "ring-primary/40");
                const dealId = e.dataTransfer.getData("deal-id");
                if (dealId) onMoveDeal(dealId, stage.id);
              }}
            >
              <div className="p-3 border-b">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${stage.color}`} />
                    <span className="font-semibold text-sm">{stage.name}</span>
                  </div>
                  <span className="text-xs text-muted-foreground bg-background px-1.5 py-0.5 rounded-md">{cardCount}</span>
                </div>
                <p className="text-xs text-muted-foreground">{totalLabel}</p>
              </div>
              <div className="p-2 space-y-2 flex-1 min-h-[100px] max-h-[calc(100vh-320px)] overflow-y-auto">
                {stageLeadPlaceholders.map(lead => (
                  <DealLeadPlaceholderCard
                    key={`lead-${lead.id}`}
                    lead={lead}
                    onClick={l => onLeadPlaceholderClick?.(l)}
                  />
                ))}
                {stageDeals.map(deal => (
                  <div
                    key={deal.id}
                    draggable
                    onDragStart={e => e.dataTransfer.setData("deal-id", deal.id)}
                  >
                    <DealCard deal={deal} onClick={onDealClick} />
                  </div>
                ))}
                {cardCount === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-6">Nenhuma negociação</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
}
